import { IMessageBox } from "../shell/types"
import { StringLike } from "../types"
import { isFunction, isString } from "../utils/utils"
import { DialogHeader } from "./DialogHeader"
import { useOmniaContext } from "./context"
import { showDialog } from "./dialog"
import * as msgbox from "../shell/msgbox/msgbox"

type Content = React.FunctionComponent<any> | React.ReactNode

/**
 * Displays a message box with "OK" and "Cancel" options.
 * @returns Returns a Promise that resolves to true if "OK" is clicked, and false otherwise.
 */
export function showOkCancel(content: Content, title?: StringLike, okText: string = "Ok", okClass: string = "btn-primary"): PromiseLike<boolean> {

    return msgbox.showOkCancel({ showMessageBox }, content, title, okText, okClass)

}

export function showYesNo(content: string, title?: StringLike) {

    return msgbox.showYesNo({ showMessageBox }, content, title)

}

export function showInfo(content: Content, title?: StringLike, okText: string = "Ok") {

    return msgbox.showInfo({ showMessageBox }, content, title, okText)

}

export function showWarning(content: Content, title?: StringLike, okText: string = "Ok") {

    return msgbox.showWarning({ showMessageBox }, content, title, okText)

}

export function showDeleteCancel(content: Content, title?: StringLike, deleteText: string = "Delete") {

    return msgbox.showDeleteCancel({ showMessageBox }, content, title, deleteText)

}

export type Msgbox = Omit<IMessageBox, "templateUrl" | "content"> & {

    content?: Content

}

export function showMessageBox<Result = boolean>(options: Msgbox) {

    const onClose = showDialog<Msgbox, Result>({
        content: MessageBox,
        contentProps: {
            ...options
        }
    })

    return onClose

}

export function showTextArea(content: string, title: string = "Info") {

    content = "<form><textarea id='omnia-debug-text-area' style='width: 100%; font-family: monospace' rows='25'>" + content + "</textarea></form>"

    return showMessageBox({

        title: title,
        content: content,
        icon: null,

        buttons: [
            { text: "Ok", value: true, cssClass: "btn-info" }
        ]

    })

}

interface MessageBoxProps extends Msgbox {
    close: (result: any) => void
}

export function MessageBox({ title, content, icon, buttons, close }: MessageBoxProps) {

    const { config } = useOmniaContext()

    title ??= config.brand.name

    return <>

        <DialogHeader icon={icon}>{title?.toString()}</DialogHeader>

        <div className="modal-body">

            {
                React.isValidElement(content)
                    ? content
                    : isString(content)
                        ? <div dangerouslySetInnerHTML={{ __html: content }} ></div>
                        : isFunction(content)
                            ? content({})
                            : <div dangerouslySetInnerHTML={{ __html: convertContentToString(content) }} ></div>
            }

        </div>

        <div className="modal-footer">
            {
                buttons.map((b, index) =>
                    <button key={index + 1} className={`btn ${b.cssClass || 'btn-default'}`} type="button" onClick={() => close(b.value)} dangerouslySetInnerHTML={{ __html: b.text }}>
                    </button>
                )
            }
        </div>

    </>

}

function convertContentToString(content: {}) {

    let text = content["text"]

    try {
        text ??= JSON.stringify(text)
    } catch (error) {
        console.error(error)
    }

    return text

}
